/** Scoring matrix for asset group criticality survey (8 questions). */

export const SURVEY_QUESTION_IDS = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8'];

export const SURVEY_OPTION_KEYS = {
    q1: ['a', 'b', 'c', 'd'],
    q2: ['a', 'b', 'c', 'd'],
    q3: ['a', 'b', 'c', 'd'],
    q4: ['a', 'b', 'c', 'd', 'e'],
    q5: ['a', 'b', 'c', 'd', 'e'],
    q6: ['a', 'b', 'c'],
    q7: ['a', 'b', 'c', 'd'],
    q8: ['a', 'b', 'c', 'd', 'e'],
};

export const SURVEY_SCORE_MATRIX = {
    q1: { a: 1, b: 2, c: 3, d: 5 },
    q2: { a: 1, b: 2, c: 3, d: 5 },
    q3: { a: 1, b: 2, c: 3, d: 5 },
    q4: { a: 1, b: 2, c: 3, d: 4, e: 5 },
    q5: { a: 1, b: 2, c: 3, d: 5, e: 6 },
    q6: { a: 1, b: 2, c: 3 },
    q7: { a: 1, b: 2, c: 3, d: 5 },
    q8: { a: 1, b: 2, c: 3, d: 5, e: 6 },
};

export const EVALUATION_STATUSES = ['undefined', 'passive', 'active', 'completed'];

export const CRITICALITY_DEGREES = [1, 2, 3];

export const MAX_SURVEY_END_DAYS = 15;

export const SURVEY_CLOSED_ERROR = 'Anket bitiş tarihi geçti; yeni yanıt kabul edilmiyor';

export function startOfLocalDay(date = new Date()) {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
}

export function parseSurveyEndDate(endDateStr) {
    if (typeof endDateStr !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(endDateStr)) {
        return null;
    }
    const [year, month, day] = endDateStr.split('-').map(Number);
    const parsed = new Date(year, month - 1, day);
    parsed.setHours(0, 0, 0, 0);
    if (
        parsed.getFullYear() !== year
        || parsed.getMonth() !== month - 1
        || parsed.getDate() !== day
    ) {
        return null;
    }
    return parsed;
}

/** True when today's local date is after the survey endDate (endDate day is still open). */
export function isSurveyClosed(endDateStr, now = new Date()) {
    const end = parseSurveyEndDate(endDateStr);
    if (!end) return false;
    return startOfLocalDay(now) > end;
}

/**
 * Validates a chosen or stored endDate against today and MAX_SURVEY_END_DAYS.
 * Used on survey start and on the response-accept path.
 */
export function validateEndDateWithinLimit(endDateStr, now = new Date()) {
    const end = parseSurveyEndDate(endDateStr);
    if (!end) {
        return 'Geçersiz anket bitiş tarihi';
    }
    const today = startOfLocalDay(now);
    const max = new Date(today);
    max.setDate(max.getDate() + MAX_SURVEY_END_DAYS);
    if (end < today) {
        return 'Anket bitiş tarihi bugünden önce olamaz';
    }
    if (end > max) {
        return `Anket bitiş tarihi en fazla ${MAX_SURVEY_END_DAYS} gün sonrası seçilebilir`;
    }
    return null;
}

/** Rejects new responses when the survey window is closed or the stored endDate is invalid. */
export function getSurveyAcceptError(endDateStr, now = new Date()) {
    if (isSurveyClosed(endDateStr, now)) {
        return SURVEY_CLOSED_ERROR;
    }
    return validateEndDateWithinLimit(endDateStr, now);
}

/**
 * @param {Record<string, string>} answers
 * @returns {{ questionScores: Record<string, number>, totalScore: number }}
 */
export function calculateSurveyScores(answers = {}) {
    const questionScores = {};
    let totalScore = 0;

    for (const qId of SURVEY_QUESTION_IDS) {
        const option = answers[qId];
        const score = SURVEY_SCORE_MATRIX[qId]?.[option];
        if (score == null) {
            throw new Error(`Invalid answer for ${qId}: ${option}`);
        }
        questionScores[qId] = score;
        totalScore += score;
    }

    return { questionScores, totalScore };
}

/**
 * @param {number} totalScore
 * @returns {1|2|3}
 */
export function calculateCriticalityDegree(totalScore) {
    if (totalScore < 18) return 1;
    if (totalScore <= 28) return 2;
    return 3;
}

/**
 * @param {Record<string, string>} answers
 * @returns {{ questionScores: Record<string, number>, totalScore: number, criticalityDegree: 1|2|3 }}
 */
export function calculateSurveyResult(answers) {
    const { questionScores, totalScore } = calculateSurveyScores(answers);
    return {
        questionScores,
        totalScore,
        criticalityDegree: calculateCriticalityDegree(totalScore),
    };
}

export function validateSurveyAnswers(answers) {
    for (const qId of SURVEY_QUESTION_IDS) {
        const option = answers?.[qId];
        if (!option || !SURVEY_SCORE_MATRIX[qId]?.[option]) {
            return { valid: false, missingQuestion: qId };
        }
    }
    return { valid: true };
}
